/**
 * Pembantu format untuk KREDIT dan UANG.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * KENAPA SEMUA HITUNGAN DI SINI MEMAKAI BILANGAN BULAT
 * ────────────────────────────────────────────────────────────────────────────
 * API mengirim kredit sebagai bilangan bulat dalam 1/100 kredit (`1250` = 12,50
 * kredit) dan uang sebagai bilangan bulat dalam sub-unit mata uang / sen
 * (`5000` + `USD` = $50,00). Bentuk itu disengaja: begitu sebuah nominal
 * disimpan sebagai pecahan desimal IEEE-754, ia berhenti menjadi nominal dan
 * berubah jadi *hampir* nominal — `0.1 + 0.2 !== 0.3`.
 *
 * Selisihnya terlalu kecil untuk terlihat di satu layar. Yang terjadi adalah ia
 * menumpuk: satu sen hilang per transaksi, ribuan transaksi kemudian, dan
 * gejalanya muncul berbulan-bulan sesudahnya sebagai "laporan tidak balance" —
 * jauh dari baris kode yang menyebabkannya, tanpa satu pun transaksi yang bisa
 * ditunjuk sebagai yang salah. Yang paling mahal bukan uangnya, tapi hilangnya
 * kepercayaan pada seluruh angka di sistem: sekali laporan meleset, tidak ada
 * cara membuktikan angka mana yang masih benar.
 *
 * Karena itu berkas ini TIDAK PERNAH menghitung dengan float. Pemisahan bagian
 * bulat dan pecahan memakai `%` lalu pengurangan (keduanya eksak untuk bilangan
 * bulat), dan float hanya muncul di langkah paling akhir — saat menyerahkan
 * nilai ke `Intl.NumberFormat` untuk DITAMPILKAN, bukan untuk dihitung.
 *
 * Aturan turunan untuk pemanggil: jangan pernah menjumlahkan/mengalikan hasil
 * `formatCredits`. Hitung dengan bilangan bulat 1/100 kredit, format sekali di
 * ujung. Harga top-up juga tidak dihitung ulang di peramban sama sekali — itu
 * datang dari `GET /me/topups/quote` (dua tempat yang menghitung hal yang sama
 * pasti akan berbeda suatu hari, dan yang terlihat pengguna adalah harga yang
 * berubah saat ia menekan tombol).
 */

/** Kredit dikirim dalam 1/100 kredit. Server juga mengirim `scale` = 100. */
export const CREDIT_SCALE = 100

/** Uang dikirim dalam sub-unit (sen) — 100 sub-unit = 1 unit. */
export const MONEY_SUBUNIT = 100

/**
 * Mata uang yang TIDAK punya sub-unit: `15000` + `IDR` berarti Rp 15.000,
 * bukan Rp 150.
 *
 * Harus ada karena rupiah dipakai orang tanpa sen dan DOKU menagih rupiah
 * bulat. Server memperlakukan IDR dengan exponent 0 supaya `amount_minor`
 * bisa diserahkan apa adanya ke penyedia — lihat `moneyExponent()` di
 * `internal/api/settings.go`. Kalau daftar ini tidak sama dengan daftar di
 * sana, halaman dompet menampilkan Rp 150 untuk tagihan Rp 15.000: seratus
 * kali lipat meleset, tanpa satu pun galat, di angka yang orang pakai untuk
 * memutuskan membayar.
 *
 * Server juga mengirim `money_digits` di setiap penawaran. Yang itu yang
 * dipakai kalau ada; daftar ini cadangan untuk muatan yang belum membawanya
 * (riwayat lama, mock).
 */
const ZERO_DECIMAL = new Set(['IDR', 'JPY', 'KRW', 'VND'])

/** Berapa digit desimal untuk satu kode mata uang. */
export const moneyDigits = (currency) =>
  ZERO_DECIMAL.has(String(currency || '').toUpperCase()) ? 0 : 2

const INTL = { id: 'id-ID', ms: 'ms-MY', ar: 'ar-EG', en: 'en-US' }
const intlLocale = (locale) => INTL[locale] || INTL.id

/** Pemisah desimal untuk kredit. Uang memakai pemisah dari Intl. */
const decimalSep = (locale) => (locale === 'en' ? '.' : ',')

/**
 * Pecah bilangan bulat jadi {neg, whole, frac} terhadap `scale`.
 * `%` dan `-` eksak untuk bilangan bulat; pembagian yang tersisa selalu punya
 * hasil bulat, jadi hasilnya juga eksak.
 */
function split(value, scale) {
  const n = Math.trunc(Number(value) || 0)
  const abs = Math.abs(n)
  const frac = abs % scale
  return { neg: n < 0, whole: (abs - frac) / scale, frac }
}

/** `1250` → `"12,50"` (`"12.50"` di locale en). Membawa tanda minus apa adanya. */
export function formatCredits(value, locale = 'id') {
  const { neg, whole, frac } = split(value, CREDIT_SCALE)
  const grouped = new Intl.NumberFormat(intlLocale(locale)).format(whole)
  return `${neg ? '-' : ''}${grouped}${decimalSep(locale)}${String(frac).padStart(2, '0')}`
}

/**
 * `"12,50"` / `"12.5"` / `"1.234,56"` → `1250` / `1250` / `123456`.
 *
 * Pemisah terakhir yang diikuti 1–2 digit dianggap desimal; pemisah lain
 * dianggap pemisah ribuan. Aturan itu perlu karena keempat locale menulis
 * angka dengan cara berbeda dan formulir yang sama dipakai semuanya —
 * `parseFloat("1.234")` akan membaca "seribu dua ratus tiga puluh empat"
 * sebagai 1,234 tanpa memberi tanda apa pun bahwa ia salah.
 *
 * Selalu mengembalikan bilangan bulat 1/100 kredit.
 */
export function parseCredits(input) {
  const raw = String(input ?? '').trim()
  if (!raw) return 0
  const neg = raw.startsWith('-')
  const cleaned = raw.replace(/[^\d.,]/g, '')
  if (!cleaned) return 0

  const tail = cleaned.match(/[.,](\d{1,2})$/)
  const wholeStr = (tail ? cleaned.slice(0, -tail[0].length) : cleaned).replace(/[.,]/g, '')
  const whole = wholeStr ? parseInt(wholeStr, 10) : 0
  const frac = tail ? parseInt(tail[1].padEnd(2, '0'), 10) : 0

  const value = whole * CREDIT_SCALE + frac
  return neg ? -value : value
}

/**
 * `5000, "USD"` → `"$50.00"`. `15000, "IDR"` → `"Rp 15.000"`.
 *
 * `minor` = sub-unit mata uang, bukan unit — dan berapa sub-unit dalam satu
 * unit BERBEDA per mata uang. Lihat `ZERO_DECIMAL` di atas: rupiah tidak punya
 * sub-unit, jadi membaginya 100 membuat setiap tagihan tampil seperseratus
 * dari nilainya.
 *
 * `digits` boleh dipaksa oleh pemanggil yang punya `money_digits` dari server.
 * Server yang memutuskan; daftar di berkas ini cuma cadangan.
 */
export function formatMoney(minor, currency = 'USD', locale = 'id', digits) {
  const d = Number.isInteger(digits) ? digits : moneyDigits(currency)
  const subunit = d === 0 ? 1 : MONEY_SUBUNIT
  const { neg, whole, frac } = split(minor, subunit)
  // Dirakit dari string desimal, bukan dari `abs / 100`: konversi string→double
  // untuk 2 angka di belakang koma dibulatkan ke double terdekat sekali saja,
  // sedangkan pembagian mewarisi galat dari operan. Ini juga titik TERAKHIR
  // sebelum tampil — tidak ada aritmetika sesudahnya.
  const value = d === 0 ? whole : Number(`${whole}.${String(frac).padStart(2, '0')}`)
  try {
    return new Intl.NumberFormat(intlLocale(locale), {
      style: 'currency',
      currency: currency || 'USD',
      // Dipaksa eksplisit. Intl punya pendapatnya sendiri tentang tiap mata
      // uang, dan untuk IDR pendapatnya berbeda antar-locale — `en-US`
      // merender "IDR 15,000.00" sementara `id-ID` merender "Rp 15.000".
      // Selisih itu muncul sebagai dua digit nol yang tiba-tiba ada saat orang
      // mengganti bahasa halaman.
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    }).format(neg ? -value : value)
  } catch {
    // Kode mata uang tak dikenal tidak boleh mematikan halaman dompet.
    return `${neg ? '-' : ''}${value} ${currency || ''}`.trim()
  }
}

/** `"12,50 kredit"` — `unit` berasal dari `t('price.creditUnit')`. */
export const creditsWithUnit = (value, unit, locale = 'id') => `${formatCredits(value, locale)} ${unit}`
